import { EntityState, createEntityAdapter } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'
import {
  CreditCompareMetricCategorySetting,
  CreditStructureCompareEntity,
} from '../../model/credit-compare.model'
import { getCompareMetricCategorySettings } from '../../utils/credit-compare.utils'
import { CreditStructureGroupFilterActions } from '../group/credit-group.actions'
import { GseStructureGroupFilterActions } from '../gse-group/gse-group.actions'
import * as fromCreditCompareActions from './credit-compare.actions'

export interface State extends EntityState<CreditStructureCompareEntity> {
  metricCategorySettings: CreditCompareMetricCategorySetting[]
}

export const adapter = createEntityAdapter<CreditStructureCompareEntity>({
  selectId: (creditStructureCompareEntity): string =>
    creditStructureCompareEntity.credit_calculation_structure_id,
})

export const initialState: State = adapter.getInitialState({
  metricCategorySettings: [],
})

const creditStructureCompareEntityReducer = createReducer(
  initialState,
  on(
    fromCreditCompareActions.GetCreditCompareMetricsForStructureSuccess,
    fromCreditCompareActions.GetCreditCompareMetricsForStructureGroupSuccess,
    (state, { creditStructureCompareEntity }) =>
      adapter.upsertOne(creditStructureCompareEntity, state),
  ),
  on(
    fromCreditCompareActions.GetCreditCompareMetricCategorySettings,
    (state, { creditStructureCompareEntity }) => {
      if (state.metricCategorySettings.length > 1) {
        return state
      }
      const metricCategorySettings = getCompareMetricCategorySettings(
        creditStructureCompareEntity.metrics,
      )
      return { ...state, metricCategorySettings }
    },
  ),
  on(fromCreditCompareActions.RemoveCreditStructureCompareEntity, (state, { structureId }) => {
    if (!state.entities[structureId]) {
      return state
    }
    return adapter.removeOne(structureId, state)
  }),
  on(
    fromCreditCompareActions.RemoveAllCreditStructureCompareEntities,
    CreditStructureGroupFilterActions.reset,
    GseStructureGroupFilterActions.reset,
    (state) => {
      return adapter.removeAll({ ...state, metricCategorySettings: [] })
    },
  ),
  on(CreditStructureGroupFilterActions.fetchStructuresSuccess, (state, { creditStructures }) => {
    const validIds = new Set(creditStructures.map((s) => s.credit_calculation_structure_id))
    const idsToRemove = (state.ids as string[]).filter((id) => !validIds.has(id))
    return idsToRemove.length > 0 ? adapter.removeMany(idsToRemove, state) : state
  }),
  on(GseStructureGroupFilterActions.fetchStructuresSuccess, (state, { gseStructures }) => {
    const validIds = new Set(gseStructures.map((s) => s.gse_calculation_structure_id))
    const idsToRemove = (state.ids as string[]).filter((id) => !validIds.has(id))
    return idsToRemove.length > 0 ? adapter.removeMany(idsToRemove, state) : state
  }),
)

export function reducer(state: State | undefined, action: Action) {
  return creditStructureCompareEntityReducer(state, action)
}
